<template>
  <BaseBlock title="Demandes en cours d'étude">
    <v-container>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Filtres de recherche
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row align="center">
              <v-col cols="2">
                <v-text-field
                  v-model="search.etablissement"
                  label="établissement"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="search.demandeur"
                  label="demandeur"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="search.avisCROP"
                  :items="getAvisCROPSelectData()"
                  label="avis du CROP"
                  clearable
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="search.avisRPO"
                  :items="getAvisRPOSelectData()"
                  label="avis du responsable de parcours"
                  clearable
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="2">
                <v-select
                  v-model="search.visiteProgrammee"
                  :items="getVisiteProgrammeeSelectData()"
                  label="visite programmée"
                  clearable
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="2">
                <v-select
                  v-model="search.visiteEffectuee"
                  :items="getVisiteEffectueeSelectData()"
                  label="visite effectuée"
                  clearable
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <SearchDateWidget
              :searchOnDate="search.searchOnDate"
              v-on:update:searchOnDate="search.searchOnDate = $event"
              :dateMin="search.dateMin"
              v-on:update:dateMin="search.dateMin = $event"
              :dateMax="search.dateMax"
              v-on:update:dateMax="search.dateMax = $event"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-data-table
        :headers="headers"
        :items="dossiersEnAttenteDAvis"
        v-if="dossiers"
      >
        <!-- template du header "voir" -->
        <template v-slot:[`header.dossier`]="{}">
          <v-icon>mdi-eye</v-icon>
        </template>
        <!-- template des cellules "voir" -->
        <template v-slot:[`item.dossier`]="{ item }">
          <LinkToPDFWidget :dossier="item" />
        </template>
        <!-- template des cellules "établissement" -->
        <template v-slot:[`item.officine.denomination`]="{ item }">
          <div class="text-body-2">{{ item.officine.denomination }}</div>
          <div class="text-caption grey--text font-italic">
            {{ item.officine.codePostal }}
            -
            {{ item.officine.ville }}
          </div>
        </template>
        <!-- template des cellules "demandeur" -->
        <template v-slot:[`item.demandeur`]="{ item }">
          <div class="text-body-2">{{ item.demandeur }}</div>
          <div class="text-caption grey--text font-italic">
            <a
              target="_blank"
              :href="
                `mailto:${item.contactMail}?subject=Pharmagrément - Suivi du dossier de demande d'agrément de: ${item.officine.denomination}`
              "
            >
              {{ item.contactMail }}
            </a>
          </div>
        </template>
        <!-- template des cellules "type" -->
        <template v-slot:[`item.premiereDemande`]="{ item }">
          <div class="text-body-2">
            {{ item.premiereDemande ? "Première demande" : "Renouvellement" }}
          </div>
        </template>
        <!-- template des cellules "date" -->
        <template v-slot:[`item.dateDepot`]="{ item }">
          <div class="text-body-2">{{ getDate(item.dateDepot) }}</div>
        </template>
        <!-- template des cellules "avisCROP" -->
        <template v-slot:[`item.avisCROP`]="{ item }">
          <div class="text-body-2">
            {{
              item.avisCROP ? getAvisLibelle(item.avisCROP.avis) : "Pas d'avis"
            }}
          </div>
          <div>
            <v-btn
              v-if="item.avisCROP && item.avisCROP.avis !== null"
              @click="openVoirAvisCROPDialog(item)"
              style="float:right"
              x-small
            >
              Voir l'avis...
            </v-btn>
          </div>
        </template>
        <!-- template des cellules "avisRPO" -->
        <template v-slot:[`item.avisRPO`]="{ item }">
          <div class="text-body-2 text-center">
            {{
              item.avisRPO ? getAvisLibelle(item.avisRPO.avis) : "Pas d'avis"
            }}
          </div>
          <div>
            <v-btn
              v-if="item.avisRPO && item.avisRPO.avis !== null"
              @click="openVoirAvisRPODialog(item)"
              style="float:right"
              x-small
            >
              Voir l'avis...
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-container>
    <!-- popup voir avis du CROP -->
    <ModalVoirAvisCROP
      :avis="currentAvisCROP"
      :display="modalVoirAvisCROPDisplay"
      v-on:update:display="modalVoirAvisCROPDisplay = $event"
      v-if="modalVoirAvisCROPDisplay"
    />
    <!-- popup voir avis du RPO -->
    <ModalVoirAvisRPO
      :avis="currentAvisRPO"
      :display="modalVoirAvisRPODisplay"
      v-on:update:display="modalVoirAvisRPODisplay = $event"
      v-if="modalVoirAvisRPODisplay"
    />
  </BaseBlock>
</template>

<script>
import { mapState } from "vuex";
import common from "@/common";
import ApiAvisCROPRepository from "@/repositories/ApiAvisCROPRepository";
import ApiAvisRPORepository from "@/repositories/ApiAvisRPORepository";
import BaseBlock from "@/components/base/BaseBlock";
import LinkToPDFWidget from "@/components/LinkToPDFWidget";
import SearchDateWidget from "@/components/SearchDateWidget";
import ModalVoirAvisCROP from "@/components/ModalVoirAvisCROP";
import ModalVoirAvisRPO from "@/components/ModalVoirAvisRPO";

export default {
  name: "DemandesEnCours",
  components: {
    BaseBlock,
    LinkToPDFWidget,
    SearchDateWidget,
    ModalVoirAvisCROP,
    ModalVoirAvisRPO
  },
  data() {
    return {
      refresh_dossier_timer: null,
      modalVoirAvisCROPDisplay: false,
      modalVoirAvisRPODisplay: false,
      currentAvisCROP: null,
      currentAvisRPO: null,
      search: {
        etablissement: "",
        demandeur: "",
        avisCROP: null,
        avisRPO: null,
        visiteProgrammee: null,
        visiteEffectuee: null,
        searchOnDate: false,
        dateMin: null,
        dateMax: null
      },
      headers: [
        {
          align: "center",
          sortable: false,
          value: "dossier"
        },
        {
          text: "Etablissement",
          align: "start",
          sortable: true,
          value: "officine.denomination"
        },
        {
          text: "Demandeur",
          align: "start",
          sortable: true,
          value: "demandeur"
        },
        {
          text: "Type",
          align: "start",
          sortable: true,
          value: "premiereDemande"
        },
        {
          text: "Date de dépot de la demande",
          align: "start",
          sortable: true,
          value: "dateDepot"
        },
        {
          text: "Avis du CROP",
          align: "start",
          sortable: false,
          value: "avisCROP"
        },
        {
          text: "Avis du responsable de parcours",
          align: "start",
          sortable: false,
          value: "avisRPO"
        }
      ]
    };
  },
  computed: {
    ...mapState(["dossiers", "user"]),
    dossiersEnAttenteDAvis() {
      return this.dossiers
        .filter(d => {
          if ("" !== this.search.etablissement) {
            if (
              !d.officine ||
              !d.officine.denomination
                .toLowerCase()
                .includes(this.search.etablissement)
            )
              return false;
          }
          if ("" !== this.search.demandeur) {
            if (!d.contactPrenom || !d.contactNom) return false;
            if (
              !(d.contactPrenom + " " + d.contactNom)
                .toLowerCase()
                .includes(this.search.demandeur)
            )
              return false;
          }
          if (null !== this.search.avisCROP) {
            if (this.search.avisCROP === 2 && d.avisCROP) return false;
            if (
              this.search.avisCROP === false &&
              (!d.avisCROP || d.avisCROP.avis === true)
            )
              return false;
            if (
              this.search.avisCROP === true &&
              (!d.avisCROP || d.avisCROP.avis === false)
            )
              return false;
          }
          if (null !== this.search.avisRPO) {
            if (
              this.search.avisRPO === 2 &&
              d.avisRPO &&
              d.avisRPO.avis !== null
            )
              return false;
            if (
              this.search.avisRPO === false &&
              (!d.avisRPO || d.avisRPO.avis === true || d.avisRPO.avis === null)
            )
              return false;
            if (
              this.search.avisRPO === true &&
              (!d.avisRPO ||
                d.avisRPO.avis === false ||
                d.avisRPO.avis === null)
            )
              return false;
          }
          if (null !== this.search.visiteProgrammee) {
            let vp = common.hasVisiteProgrammee(d);
            if (false === this.search.visiteProgrammee && false !== vp)
              return false;
            if (true === this.search.visiteProgrammee && true !== vp)
              return false;
          }
          if (null !== this.search.visiteEffectuee) {
            let ve = common.hasVisiteEffectuee(d);
            if (false === this.search.visiteEffectuee && false !== ve)
              return false;
            if (true === this.search.visiteEffectuee && true !== ve)
              return false;
          }
          if (
            this.search.searchOnDate &&
            !common.matchDate(d, this.search.dateMin, this.search.dateMax)
          )
            return false;
          return common.isAttente(d);
        })
        .map(d => {
          d.demandeur = d.contactPrenom + " " + d.contactNom;
          return d;
        });
    }
  },
  async created() {
    if (null === this.dossiers) await this.loadDossiers();
    else this.loadDossiers();
    this.refresh_dossier_timer = setInterval(
      this.loadDossiers,
      common.REFRESH_TIMER
    );
  },
  beforeDestroy() {
    clearInterval(this.refresh_dossier_timer);
  },
  methods: {
    async loadDossiers() {
      this.$store.dispatch("loadDossiers");
    },
    getDate(timestamp) {
      return common.formatDate(timestamp);
    },
    getAvisLibelle(avis) {
      return common.getAvisLibelle(avis);
    },
    getAvisCROPSelectData() {
      return common.getAvisCROPSelectData();
    },
    getAvisRPOSelectData() {
      return common.getAvisRPOSelectData();
    },
    getVisiteProgrammeeSelectData() {
      return common.getVisiteProgrammeeSelectData();
    },
    getVisiteEffectueeSelectData() {
      return common.getVisiteEffectueeSelectData();
    },
    envoyerMailInscription(dossier) {
      common.sendMailInscription(dossier);
      this.$toast.success("Le code a été renvoyé.");
    },
    async openVoirAvisCROPDialog(dossier) {
      if (
        !(
          this.currentAvisCROP &&
          dossier.avisCROP &&
          this.currentAvisCROP.id === dossier.avisCROP.id
        )
      )
        this.currentAvisCROP = await ApiAvisCROPRepository.get(
          dossier.avisCROP.id
        );
      this.modalVoirAvisCROPDisplay = true;
    },
    async openVoirAvisRPODialog(dossier) {
      if (
        !(
          this.currentAvisRPO &&
          dossier.avisRPO &&
          this.currentAvisRPO.id === dossier.avisRPO.id
        )
      )
        this.currentAvisRPO = await ApiAvisRPORepository.get(
          dossier.avisRPO.id
        );
      this.modalVoirAvisRPODisplay = true;
    }
  }
};
</script>
